import React, { useState, useEffect } from 'react';
import axios from '../../services/axios';
import {
  ThemeProvider,
  createTheme,
  Box,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Fab,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Typography,
  Stack,
  Snackbar, // NUEVO
  Alert,    // NUEVO
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Image as ImageIcon,
  FileUpload as UploadIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: { main: '#0B74DA' },
    background: { default: '#F5F7FA', paper: '#ffffff' },
  },
  typography: { h4: { fontWeight: 700 } },
});

const WhiteTableHead = styled(TableHead)(() => ({
  backgroundColor: '#f0f2f5',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    color: '#333',
    fontSize: '0.9rem',
  },
}));

const StyledTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(15,30,60,0.02)',
  '&:hover': { backgroundColor: 'rgba(15,30,60,0.05)' },
}));

export default function Slides() {
  const [slides, setSlides] = useState([]);
  const [meta, setMeta] = useState({ total: 0, per_page: 10, page: 0 });
  const [search, setSearch] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  // NUEVO: Estado para notificaciones elegantes
  const [notification, setNotification] = useState({ 
    open: false, 
    message: "", 
    severity: "success" 
  });

  const [form, setForm] = useState({ id: '', title: '', subtitle: '', image_url: '', order: 0 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [currentIdToDelete, setCurrentIdToDelete] = useState(null);

  useEffect(() => {
    fetchSlides();
  }, []);

  const fetchSlides = async (page = 0, perPage = 10, searchText = '') => {
    try {
      const response = await axios.get('/slides', { params: { page: page + 1, per_page: perPage } });
      let list = response.data || [];

      if (searchText) {
        const lowered = searchText.toLowerCase();
        list = list.filter(
          (s) =>
            (s.title || '').toLowerCase().includes(lowered) ||
            (s.subtitle || '').toLowerCase().includes(lowered)
        );
      }

      setSlides(list);
      setMeta({ total: list.length, per_page: perPage, page });
    } catch (error) {
      console.error('Error fetching slides:', error);
    }
  };

  // --- MANEJO DE NOTIFICACIONES ---
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setForm((prev) => ({ ...prev, image_url: URL.createObjectURL(file) }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('title', form.title || '');
    formData.append('subtitle', form.subtitle || '');
    formData.append('image_url', form.image_url || '');
    formData.append('order', form.order || 0);

    if (selectedFile) {
        formData.append("foto_file", selectedFile);
    }

    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };

      if (editing) {
        formData.append("_method", "PUT");
        await axios.post(`/slides/${form.id}`, formData, config);
      } else {
        await axios.post('/slides', formData, config);
      }

      setNotification({ open: true, message: "¡Slide guardado exitosamente!", severity: "success" });
      fetchSlides(meta.page, meta.per_page, search);
      setFormOpen(false);
      setSelectedFile(null);
    } catch (error) {
      if (error.response && error.response.status === 422) {
        setNotification({ 
          open: true, 
          message: "Error: La imagen debe ser jpg, png o webp y pesar menos de 2MB", 
          severity: "error" 
        });
      } else {
        setNotification({ open: true, message: "Error al guardar el slide", severity: "error" });
      }
    }
  };

  const openNew = () => {
    setSelectedFile(null);
    setForm({ id: '', title: '', subtitle: '', image_url: '', order: 0 });
    setEditing(false);
    setFormOpen(true);
  };

  const handleEdit = (slide) => {
    setSelectedFile(null);
    setForm({ ...slide });
    setEditing(true);
    setFormOpen(true);
  };

  const openDelete = (id) => {
    setCurrentIdToDelete(id);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/slides/${currentIdToDelete}`);
      setNotification({ open: true, message: "Slide eliminado correctamente", severity: "success" });
      fetchSlides(meta.page, meta.per_page, search);
      setDeleteOpen(false);
    } catch (error) {
      setNotification({ open: true, message: "Error al eliminar el slide", severity: "error" });
    }
  };

  const handleChangePage = (_, newPage) => {
    fetchSlides(newPage, meta.per_page, search);
  };

  const handleChangeRowsPerPage = (e) => {
    const per = parseInt(e.target.value, 10);
    fetchSlides(0, per, search);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearch(value);
    fetchSlides(0, meta.per_page, value);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 3, bgcolor: 'background.default', minHeight: '100vh' }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardHeader
            title={<Typography variant="h4">Carrusel (Slides)</Typography>}
            action={
              <Fab color="primary" size="small" onClick={openNew}>
                <AddIcon />
              </Fab>
            }
          />

          <CardContent>
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  value={search}
                  onChange={handleSearch}
                  fullWidth
                  placeholder="Buscar por título o subtítulo..."
                  InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} /> }}
                />
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: 2 }}>
              <Table stickyHeader size="small">
                <WhiteTableHead>
                  <TableRow>
                    <TableCell>Imagen</TableCell>
                    <TableCell>Título</TableCell>
                    <TableCell>Subtítulo</TableCell>
                    <TableCell align="center">Orden</TableCell>
                    <TableCell align="center">Acciones</TableCell>
                  </TableRow>
                </WhiteTableHead>
                <TableBody>
                  {slides.map((slide, idx) => (
                    <StyledTableRow key={slide.id} index={idx}>
                      <TableCell>
                        <Avatar src={slide.image_url} variant="rounded" sx={{ width: 80, height: 45 }}>
                          <ImageIcon />
                        </Avatar>
                      </TableCell>
                      <TableCell>{slide.title}</TableCell>
                      <TableCell>{slide.subtitle}</TableCell>
                      <TableCell align="center">{slide.order}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton size="small" onClick={() => handleEdit(slide)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => openDelete(slide.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </Stack>
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={meta.total}
                page={meta.page}
                onPageChange={handleChangePage}
                rowsPerPage={meta.per_page}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas:"
              />
            </TableContainer>
          </CardContent>
        </Card>

        {/* DIALOG FORMULARIO */}
        <Dialog open={formOpen} fullWidth maxWidth="sm" onClose={() => setFormOpen(false)}>
          <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Editar Slide' : 'Nuevo Slide'}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  name="image_url"
                  label="URL de Imagen o selecciona archivo"
                  value={form.image_url}
                  onChange={handleInputChange}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <input 
                          accept="image/jpeg,image/png,image/jpg,image/webp" 
                          style={{ display: 'none' }} 
                          id="slide-file-upload" 
                          type="file" 
                          onChange={handleFileChange} 
                        />
                        <label htmlFor="slide-file-upload">
                          <IconButton color="primary" component="span"><UploadIcon /></IconButton>
                        </label>
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              <Grid item xs={12}><TextField name="title" label="Título" value={form.title} onChange={handleInputChange} fullWidth /></Grid>
              <Grid item xs={12}><TextField name="subtitle" label="Subtítulo" value={form.subtitle} onChange={handleInputChange} fullWidth /></Grid>
              <Grid item xs={12}><TextField name="order" label="Orden" type="number" value={form.order} onChange={handleInputChange} fullWidth /></Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setFormOpen(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} variant="contained" sx={{ px: 3 }}>Guardar</Button>
          </DialogActions>
        </Dialog>

        {/* NOTIFICACIÓN SNACKBAR */}
        <Snackbar 
          open={notification.open} 
          autoHideDuration={5000} 
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleCloseNotification} 
            severity={notification.severity} 
            variant="filled" 
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>

        {/* DELETE DIALOG */}
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle>¿Confirmar eliminación?</DialogTitle>
          <DialogActions>
            <Button onClick={() => setDeleteOpen(false)}>Cancelar</Button>
            <Button color="error" variant="contained" onClick={handleDelete}>Eliminar</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}